/**
 * The catalog — what a quote is built out of.
 *
 * `docs/catalog-and-packages.md` §6.2. Seven tabs, in the order the catalog is
 * actually used rather than the order it is built: **packages** first, because
 * a package is what the company sells, then the services that hold lines, then
 * the four priced things a line names, then the terms a quote carries.
 *
 * The vocabulary the catalog is *described* in — categories, units,
 * measurement types — moved to Catalog setup, and divisions to their own
 * settings page. They were tabs here, which made the strip nine deep and
 * buried the four anybody opens daily behind three nobody opens in a year.
 *
 * Every tab is the same list engine Contacts and Jobs use (plan §3.5), in
 * `embedded` chrome so this page keeps its own tabs. Nothing here opens a
 * screen of its own except the package builder: a tab is a list, and a row is
 * a form.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { RecordTabs } from '../record/record-tabs.js'
import { CatalogList } from '../components/catalog-list.js'
import { LaborForm } from '../components/forms/labor-form.js'
import { BuyingForm } from '../components/forms/buying-form.js'
import {
  ConversionCell, MarginPill, NameWithTag, conversionShort, rateCell,
} from '../components/catalog-cells.js'
import { HIRED_IN, OWNED } from '../components/forms/catalog-copy.js'
import { ItemsList } from '../components/items-list.js'
import { ServicePopout } from '../builder/popovers/service-popout.js'
import { TermsEditor } from '../components/terms-editor.js'
import { Tag } from '../components/primitives.js'
import { KindTile } from '../builder/kind-tile.js'
import { formatNumber, formatRate } from '../lib/format.js'
import { request } from '../lib/api.js'
import type { Division, MeasurementType } from '../lib/catalog-types.js'
import type { ListRow } from '../lib/use-list.js'
import { useSettingsBar } from '../settings/settings-bar.js'

/*
 * The glyph on each tab is the glyph its rows wear on a quote: a box for a
 * package, layers for a service, the `KindGlyph` family for the four priced
 * things. One picture per idea, wherever it is drawn.
 */
const TABS = [
  { key: 'packages', label: 'Packages', icon: 'package' as const, area: 'catalog-admin/packages' },
  { key: 'services', label: 'Services', icon: 'service' as const, area: 'catalog-admin/services' },
  { key: 'items', label: 'Materials', icon: 'category' as const, area: 'items' },
  { key: 'labor', label: 'Labor', icon: 'crew' as const, area: 'catalog-admin/labor-rates' },
  { key: 'equipment', label: 'Equipment', icon: 'equipment' as const,
    area: 'catalog-admin/equipment-catalog' },
  { key: 'deployment', label: 'Fees', icon: 'money' as const,
    area: 'catalog-admin/deployment-costs' },
  { key: 'terms', label: 'Terms', icon: 'files' as const, area: 'catalog-admin/terms' },
]

/** The bold line and the muted one under it, for a kind whose sub-line is prose. */
const nameAnd = (secondary: (row: ListRow) => string | undefined) => (row: ListRow) => ({
  primary: String(row.name ?? ''),
  secondary: secondary(row),
})

export function Catalog({ me }: { me: MeResponse }) {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const tab = params.get('tab') ?? 'packages'

  const [divisions, setDivisions] = useState<Division[]>([])
  const [types, setTypes] = useState<MeasurementType[]>([])
  const [reloadKey, setReloadKey] = useState(0)

  const canEdit = me.permissions.company_settings === 'edit'
    || me.permissions.company_settings === 'full'

  // Everything else hangs off a division or a measurement type, so the
  // pickers' options come from one place and refresh when those change.
  const loadOptions = useCallback(async () => {
    const [divisionRows, typeRows] = await Promise.all([
      request<{ items: Division[] }>('/catalog-admin/divisions').catch(() => ({ items: [] })),
      request<{ items: MeasurementType[] }>('/catalog-admin/measurement-types')
        .catch(() => ({ items: [] })),
    ])
    setDivisions(divisionRows.items)
    setTypes(typeRows.items)
  }, [])

  useEffect(() => { void loadOptions() }, [loadOptions, reloadKey])

  const changed = () => setReloadKey((key) => key + 1)

  /*
   * The number on each tab, before anybody opens it.
   *
   * `packages-catalog.html` prints "Packages 28 · Services 46 · Materials
   * 312"; a tab strip with no numbers makes you click all seven to find out
   * which one has anything in it. Seven cheap `count(*)`s, in parallel, and a
   * tab that fails to answer simply has no pill rather than a zero — a zero
   * is a claim, and "the request failed" is not the same claim as "there are
   * none".
   */
  const [counts, setCounts] = useState<Record<string, number>>({})
  useEffect(() => {
    let live = true
    void Promise.all(TABS.map(async (entry) => {
      const result = await request<{ count: number }>(`/${entry.area}/count`)
        .catch(() => null)
      return [entry.key, result?.count] as const
    })).then((pairs) => {
      if (!live) return
      setCounts(Object.fromEntries(
        pairs.filter((pair): pair is readonly [string, number] => pair[1] !== undefined)))
    })
    return () => { live = false }
  }, [reloadKey])

  const tabs = useMemo(
    () => TABS.map((entry) => ({ ...entry, count: counts[entry.key] })), [counts])

  // The shell draws the bar; this is what it says on this page (mockup
  // `packages-catalog.html`). No second `<PageBar>` here.
  useSettingsBar({
    title: 'Catalog',
    sub: 'what a quote is built out of · categories, units and measurement types are '
      + 'under Catalog setup',
  })

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <RecordTabs tabs={tabs} active={tab} onChange={(key) => setParams({ tab: key })} />

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {!canEdit && (
          <p className="mb-3 rounded-card border border-line bg-surface-alt p-3 text-cell text-text-muted">
            You can see the catalog because you can quote from it. Changing it needs company
            settings, because a markup is a company decision.
          </p>
        )}


        {tab === 'packages' && (
          <CatalogList
            area="catalog-admin/packages"
            title="Package"
            singular="package"
            canEdit={canEdit}
            importOrgUnitId={me.scope.selected[0]}
            reloadKey={reloadKey}
            mark={{ width: 34, render: () => <KindTile kind="package" size={24} /> }}
            primary={nameAnd((row) => String(row.description ?? '') || undefined)}
            cell={{
              pricing: (row) => (row.pricing === 'Flat'
                ? <Tag tone="warn">Flat</Tag>
                : <Tag>From its lines</Tag>),
              flatPrice: (row) => money(row.flatPrice),
            }}
            blank={{}}
            fields={[]}
            /*
             * A package is not a form. It is visits holding services holding
             * lines, which is a builder — so the row opens one rather than a
             * slide-over with forty fields in it.
             */
            open={(id) => navigate(`/settings/catalog/packages/${id}`)}
            emptyTitle="No packages yet"
            emptyBody="A package is what you actually sell: drop the dumpster off, swap it every week, pick it up. One thing to add to a quote, and every trip it needs comes with it."
          />
        )}

        {tab === 'labor' && (
          <LaborTab me={me} canEdit={canEdit} reloadKey={reloadKey} changed={changed} />
        )}

        {tab === 'items' && <ItemsList me={me} canEdit={canEdit} reloadKey={reloadKey} />}



        {tab === 'equipment' && (
          /*
           * Owned and hired-in in one list (§7, Board 3); the Owned and Hired
           * in chips are two saved views over `source`. An owned row is a kind
           * of machine the fleet has (0129) — priced here, once for every
           * machine of the type — so every row opens the same form.
           */
          <CatalogList
            area="catalog-admin/equipment-catalog"
            title="Name"
            kind="equipment"
            singular="hired-in item"
            canEdit={canEdit}
            importOrgUnitId={me.scope.selected[0]}
            reloadKey={reloadKey}
            alsoFetch={['source', 'sub', 'conversion', 'sells', 'marginPct']}
            mark={{ width: 44, render: () => <KindTile kind="equipment" size={28} /> }}
            primary={(row) => ({
              primary: (
                <NameWithTag
                  name={String(row.name ?? '')}
                  tag={row.source === 'owned' ? OWNED : HIRED_IN}
                  tone={row.source === 'owned' ? 'ok' : 'info'}
                />
              ),
              secondary: String(row.sub ?? '') || undefined,
            })}
            cell={{
              conversion: (row) => <ConversionCell text={row.conversion} />,
              cost: (row) => rateCell(row.cost),
              sells: (row) => rateCell(row.sells),
              marginPct: (row) => <MarginPill pct={row.marginPct} />,
            }}
            cardLine={(row) => cardOf(row, row.sells)}
            /* An owned kind stays while the fleet has one; Active switches it off. */
            removePath={(row) => (row.source === 'owned'
              ? null : `/catalog-admin/rentals/${String(row.id)}`)}
            blank={{}}
            fields={[]}
            editor={({ id, onClose, onSaved }) => (
              <BuyingForm kind="equipment" id={id} canEdit={canEdit}
                available={me.scope.available} onClose={onClose}
                onSaved={(message, again) => (again ? changed() : onSaved(message))} />
            )}
            emptyTitle="No equipment yet"
            emptyBody="Hired-in equipment is bought by the week or the day from a hire shop. Each kind of machine you own appears here once your fleet has one, priced for every machine of that kind."
          />
        )}

        {tab === 'deployment' && (
          <DeploymentTab me={me} canEdit={canEdit} reloadKey={reloadKey} changed={changed} />
        )}

        {tab === 'services' && (
          <CatalogList
            area="catalog-admin/services"
            title="Service"
            singular="service"
            canEdit={canEdit}
            importOrgUnitId={me.scope.selected[0]}
            reloadKey={reloadKey}
            mark={{ width: 34, render: () => <KindTile kind="service" size={24} /> }}
            primary={nameAnd((row) => String(row.description ?? '') || undefined)}
            cell={{
              pricedBy: (row) => (row.pricingMode === 'measurement'
                ? (
                  <Tag tone="ok">
                    {String(row.pricedBy ?? '')}
                  </Tag>
                )
                : <Tag>{String(row.pricedBy ?? '')}</Tag>),
              minCharge: (row) => money(row.minCharge),
            }}
            blank={{}}
            fields={[]}
            editor={({ id, onClose, onSaved }) => (
              <ServicePopout
                serviceId={id}
                divisions={divisions}
                measurementTypes={types}
                available={me.scope.available}
                onClose={onClose}
                onSaved={onSaved}
              />
            )}
            emptyTitle="No services yet"
            emptyBody="A service is the recipe an estimator reaches for: so much labor, so much material, the dump fee. Build one and quoting stops being arithmetic."
          />
        )}

        {tab === 'terms' && (
          <CatalogList
            area="catalog-admin/terms"
            title="Terms of service"
            kind="service"
            singular="set of terms"
            canEdit={canEdit}
            importOrgUnitId={me.scope.selected[0]}
            reloadKey={reloadKey}
            onChanged={changed}
            primary={nameAnd((row) => String(row.preview ?? '') || undefined)}
            cell={{
              isDefault: (row) => (row.isDefault
                ? <Tag tone="ok">Default</Tag>
                : <span className="text-text-faint">—</span>),
            }}
            blank={{}}
            fields={[]}
            editor={({ id, onClose, onSaved }) => (
              <TermsEditor
                termsId={id}
                divisions={divisions}
                onClose={onClose}
                onSaved={onSaved}
              />
            )}
            emptyTitle="No terms yet"
            emptyBody="A quote is an offer, and an offer has conditions. Write them once here and every quote carries them — payment, weather, what happens when the work changes."
          />
        )}


      </div>
    </div>
  )
}

/** A money cell that keeps its cents: a catalog is unit prices (§3.8). */
const money = (value: unknown) =>
  value === null || value === undefined || value === ''
    ? <span className="text-text-faint">—</span>
    : formatRate(Number(value))

/**
 * Labor — Board 3's columns, Board 1's form (§6–§7). A crew hour is paid by
 * the person hour, so the conversion column reads `1 crew hour = 4 person
 * hours`.
 */
function LaborTab({ me, canEdit, reloadKey, changed }: {
  me: MeResponse
  canEdit: boolean
  reloadKey: number
  changed: () => void
}) {
  return (
    <CatalogList
      area="catalog-admin/labor-rates"
      title="Name"
      kind="labor"
      singular="labor rate"
      canEdit={canEdit}
      importOrgUnitId={me.scope.selected[0]}
      reloadKey={reloadKey}
      alsoFetch={['description', 'conversion', 'billedRate', 'marginPct']}
      mark={{ width: 44, render: () => <KindTile kind="labor" size={28} /> }}
      primary={nameAnd((row) => String(row.description ?? '') || undefined)}
      cell={{
        conversion: (row) => <ConversionCell text={row.conversion} />,
        billedRate: (row) => rateCell(row.billedRate),
        costRate: (row) => rateCell(row.costRate),
        marginPct: (row) => <MarginPill pct={row.marginPct} />,
      }}
      cardLine={(row) => cardOf(row, row.billedRate)}
      blank={{}}
      fields={[]}
      editor={({ id, onClose, onSaved }) => (
        <LaborForm id={id} canEdit={canEdit} available={me.scope.available} onClose={onClose}
          onSaved={(message, again) => (again ? changed() : onSaved(message))} />
      )}
      emptyTitle="No labor rates yet"
      emptyBody="A labor rate is a crew hour: what it costs us and what it bills at. Estimating is per crew hour, not per person hour, which is why the crew size lives on the rate."
    />
  )
}

/** Fees — named what the quote calls them (Board 3). */
function DeploymentTab({ me, canEdit, reloadKey, changed }: {
  me: MeResponse
  canEdit: boolean
  reloadKey: number
  changed: () => void
}) {
  return (
    <CatalogList
      area="catalog-admin/deployment-costs"
      title="Name"
      kind="deployment"
      singular="fee"
      canEdit={canEdit}
      importOrgUnitId={me.scope.selected[0]}
      reloadKey={reloadKey}
      alsoFetch={['description', 'conversion', 'price', 'marginPct']}
      mark={{ width: 44, render: () => <KindTile kind="deployment" size={28} /> }}
      primary={nameAnd((row) => String(row.description ?? '') || undefined)}
      cell={{
        conversion: (row) => <ConversionCell text={row.conversion} />,
        cost: (row) => rateCell(row.cost),
        price: (row) => rateCell(row.price),
        marginPct: (row) => <MarginPill pct={row.marginPct} />,
      }}
      cardLine={(row) => cardOf(row, row.price)}
      blank={{}}
      fields={[]}
      editor={({ id, onClose, onSaved }) => (
        <BuyingForm kind="fee" id={id} canEdit={canEdit} available={me.scope.available}
          onClose={onClose}
          onSaved={(message, again) => (again ? changed() : onSaved(message))} />
      )}
      emptyTitle="No fees yet"
      emptyBody="Travel, fuel, dump fees, permits, a roll-off — turning up and clearing away. They are the costs a small job cannot absorb."
    />
  )
}

/**
 * The phone card's third line (Board 18 cell 1): the conversion's short form,
 * then the price with its margin under it on the right.
 */
function cardOf(row: ListRow, sells: unknown) {
  return {
    facts: [conversionShort(row.conversion)].filter((one): one is string => Boolean(one)),
    trailing: (
      <span className="flex flex-col items-end leading-tight">
        <span className="tnum font-bold text-text">{rateCell(sells)}</span>
        {row.marginPct !== null && row.marginPct !== undefined && (
          <span className="tnum text-[11.5px] text-accent">{formatNumber(Number(row.marginPct))}%</span>
        )}
      </span>
    ),
  }
}
